import { API_BASE_URL } from '../config';
import React, { useRef, useState } from 'react';
import Vapi from '@vapi-ai/web';
import './AIVoiceCall.css';

export default function AIVoiceCall() {
  const [isOpen, setIsOpen]       = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [callActive, setCallActive] = useState(false);
  const [isSpeaking, setIsSpeaking] = useState(false);
  const [isMuted, setIsMuted]     = useState(false);
  const [activeTranscript, setActiveTranscript] = useState({ text: '', role: '' });

  const vapiRef = useRef(null);

  /* â”€â”€ open / close modal â”€â”€ */
  const openModal  = () => setIsOpen(true);
  const closeModal = () => {
    if (callActive) endCall();
    setIsOpen(false);
  };

  /* â”€â”€ start call â”€â”€ */
  const startCall = async () => {
    setIsLoading(true);
    try {
      const res  = await fetch(`${API_BASE_URL}/api/call/initiate`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ userId: 'website_visitor' }),
      });
      const data = await res.json();

      if (!data.success) {
        alert('Failed to start call: ' + data.error);
        setIsLoading(false);
        return;
      }

      // Vite uses import.meta.env, not process.env
      const publicKey = (typeof import.meta !== 'undefined' && import.meta.env?.VITE_VAPI_PUBLIC_KEY)
        || process.env.REACT_APP_VAPI_PUBLIC_KEY
        || 'ad7dbfe1-9026-44c2-8782-a41c7efb7530'; // fallback: your Vapi public key

      const vapi = new Vapi(publicKey);
      vapiRef.current = vapi;

      vapi.on('call-start',  () => { setCallActive(true);  setIsLoading(false); });
      vapi.on('call-end',    () => { setCallActive(false); setIsSpeaking(false); setActiveTranscript({ text: '', role: '' }); });
      vapi.on('speech-start',() => setIsSpeaking(true));
      vapi.on('speech-end',  () => setIsSpeaking(false));
      vapi.on('message',     (msg) => {
        if (msg.type === 'transcript' && msg.transcript) {
          setActiveTranscript({ text: msg.transcript, role: msg.role });
        }
      });
      vapi.on('error',       (e) => { console.error(e); setIsLoading(false); });

      // Start call using pre-created assistantId â€” most reliable Vapi approach
      vapi.start(data.assistantId);
    } catch (err) {
      console.error('Error starting call:', err);
      alert('Could not connect. Make sure the backend is running.');
      setIsLoading(false);
    }
  };

  /* â”€â”€ end call â”€â”€ */
  const endCall = () => {
    vapiRef.current?.stop();
    setCallActive(false);
    setIsSpeaking(false);
    setIsMuted(false);
    setActiveTranscript({ text: '', role: '' });
  };

  /* â”€â”€ mute toggle â”€â”€ */
  const toggleMute = () => {
    const next = !isMuted;
    setIsMuted(next);
    vapiRef.current?.setMuted(next);
  };

  /* â”€â”€ status text â”€â”€ */
  const statusHeading = isLoading  ? 'Connectingâ€¦'
                      : callActive ? (isSpeaking ? 'AI is speakingâ€¦' : 'Listeningâ€¦')
                      : 'Talk to AI Support';

  const statusSub = isLoading  ? 'Setting up your call, please wait.'
                  : callActive ? 'Your voice call with TSIL AI is live.'
                  : 'Ask anything about our services, projects or team.';

  return (
    <>
      {/* â”€â”€ Floating Action Button â”€â”€ */}
      <div className="voice-fab-container">
        <button
          id="voice-call-fab"
          className="voice-fab-btn"
          onClick={openModal}
          aria-label="Open AI Voice Call"
        >
          {/* Phone icon */}
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07A19.5 19.5 0 0 1 4.07 12 19.79 19.79 0 0 1 1 3.18 2 2 0 0 1 2.96 1h3a2 2 0 0 1 2 1.72c.127.96.36 1.903.7 2.81a2 2 0 0 1-.45 2.11L7.09 8.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45c.907.34 1.85.573 2.81.7A2 2 0 0 1 22 16.92z"/>
          </svg>
          <span className="voice-fab-badge" />
        </button>
        <span className="voice-fab-label">AI Voice Chat</span>
      </div>

      {/* â”€â”€ Modal â”€â”€ */}
      {isOpen && (
        <div className="voice-modal-overlay" onClick={(e) => { if (e.target === e.currentTarget) closeModal(); }}>
          <div className="voice-modal" role="dialog" aria-modal="true" aria-label="AI Voice Support">

            {/* Header */}
            <div className="voice-modal-header">
              <div className="voice-modal-title-wrap">
                <span className={`voice-modal-dot ${callActive ? '' : 'inactive'}`} />
                <div>
                  <div className="voice-modal-title">TSIL AI Voice Support</div>
                  <div className="voice-modal-subtitle">{callActive ? 'Call in progress' : 'Ready to connect'}</div>
                </div>
              </div>
              <button className="voice-modal-close" onClick={closeModal} aria-label="Close">
                <svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round">
                  <line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/>
                </svg>
              </button>
            </div>

            {/* Body */}
            <div className="voice-modal-body">

              {/* Animated Orb */}
              <div className={`voice-orb-wrap ${isSpeaking ? 'speaking' : ''}`}>
                <div className="voice-orb-ring" />
                <div className="voice-orb-ring" />
                <div className="voice-orb-ring" />
                <div className="voice-orb-core">
                  {callActive ? (
                    /* Mic icon when active */
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M12 1a3 3 0 0 0-3 3v8a3 3 0 0 0 6 0V4a3 3 0 0 0-3-3z"/>
                      <path d="M19 10v2a7 7 0 0 1-14 0v-2"/>
                      <line x1="12" y1="19" x2="12" y2="23"/>
                      <line x1="8" y1="23" x2="16" y2="23"/>
                    </svg>
                  ) : (
                    /* Phone icon when idle */
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07A19.5 19.5 0 0 1 4.07 12 19.79 19.79 0 0 1 1 3.18 2 2 0 0 1 2.96 1h3a2 2 0 0 1 2 1.72c.127.96.36 1.903.7 2.81a2 2 0 0 1-.45 2.11L7.09 8.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45c.907.34 1.85.573 2.81.7A2 2 0 0 1 22 16.92z"/>
                    </svg>
                  )}
                </div>
              </div>

              {/* Status Text & Live Transcript */}
              <div className="voice-status-text">
                <h3>{statusHeading}</h3>
                <p>{statusSub}</p>
              </div>

              {callActive && activeTranscript.text && (
                <div style={{
                  marginTop: '15px', padding: '12px 16px', borderRadius: '12px',
                  backgroundColor: activeTranscript.role === 'user' ? 'rgba(59,130,246,0.15)' : 'rgba(168,85,247,0.15)',
                  border: activeTranscript.role === 'user' ? '1px solid rgba(59,130,246,0.3)' : '1px solid rgba(168,85,247,0.3)',
                  color: '#fff', fontSize: '14px', width: '90%', textAlign: 'center', fontStyle: 'italic',
                  minHeight: '40px', display: 'flex', alignItems: 'center', justifyContent: 'center'
                }}>
                  <strong style={{ opacity: 0.7, marginRight: '6px' }}>{activeTranscript.role === 'user' ? 'You: ' : 'AI: '}</strong>
                  "{activeTranscript.text}"
                </div>
              )}

              {/* Controls */}
              {callActive ? (
                <div className="voice-controls">
                  {/* Mute */}
                  <button
                    className={`voice-ctrl-btn ${isMuted ? 'muted' : ''}`}
                    onClick={toggleMute}
                    aria-label={isMuted ? 'Unmute' : 'Mute'}
                    title={isMuted ? 'Unmute' : 'Mute'}
                  >
                    {isMuted ? (
                      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <line x1="1" y1="1" x2="23" y2="23"/>
                        <path d="M9 9v3a3 3 0 0 0 5.12 2.12M15 9.34V4a3 3 0 0 0-5.94-.6"/>
                        <path d="M17 16.95A7 7 0 0 1 5 12v-2m14 0v2a7 7 0 0 1-.11 1.23"/>
                        <line x1="12" y1="19" x2="12" y2="23"/><line x1="8" y1="23" x2="16" y2="23"/>
                      </svg>
                    ) : (
                      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <path d="M12 1a3 3 0 0 0-3 3v8a3 3 0 0 0 6 0V4a3 3 0 0 0-3-3z"/>
                        <path d="M19 10v2a7 7 0 0 1-14 0v-2"/>
                        <line x1="12" y1="19" x2="12" y2="23"/><line x1="8" y1="23" x2="16" y2="23"/>
                      </svg>
                    )}
                  </button>

                  {/* End Call */}
                  <button className="voice-end-btn" onClick={endCall} aria-label="End Call">
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M10.68 13.31a16 16 0 0 0 3.41 2.6l1.27-1.27a2 2 0 0 1 2.11-.45c.907.34 1.85.57 2.81.7A2 2 0 0 1 22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.42 19.42 0 0 1-3.33-2.67m-2.67-3.34a19.79 19.79 0 0 1-3.07-8.63A2 2 0 0 1 3.18 1h3a2 2 0 0 1 2 1.72c.127.96.36 1.9.7 2.81a2 2 0 0 1-.45 2.11L7.09 8.91"/>
                      <line x1="23" y1="1" x2="1" y2="23"/>
                    </svg>
                    End Call
                  </button>
                </div>
              ) : (
                <button
                  className="voice-start-btn"
                  onClick={startCall}
                  disabled={isLoading}
                  aria-label="Start Voice Call"
                >
                  {isLoading ? (
                    <><span className="voice-spinner" /> Connectingâ€¦</>
                  ) : (
                    <>
                      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
                        <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07A19.5 19.5 0 0 1 4.07 12 19.79 19.79 0 0 1 1 3.18 2 2 0 0 1 2.96 1h3a2 2 0 0 1 2 1.72c.127.96.36 1.903.7 2.81a2 2 0 0 1-.45 2.11L7.09 8.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45c.907.34 1.85.573 2.81.7A2 2 0 0 1 22 16.92z"/>
                      </svg>
                      Start Voice Call
                    </>
                  )}
                </button>
              )}
            </div>

            {/* Footer */}
            <div className="voice-modal-footer">
              Powered by Vapi AI &nbsp;Â·&nbsp; Microphone required
            </div>
          </div>
        </div>
      )}
    </>
  );
}

